import type { CmmdRuntimeThreadReplica } from "@cmmd-forge/contracts";
import { decodeCmmdRuntimeWorktreeLaunchActivityKind } from "@cmmd-forge/contracts";

import {
  deriveWorktreeLaunchProgress,
  type LaunchStop,
  type WorktreeLaunchProgress,
} from "./worktreeLaunchProgress";

/**
 * Rebuilds the same launch state shown on a direct runtime route from the
 * durable cloud replica. The replica only transports launch rows as tool calls
 * because the forge-context event contract has no separate launch event kind.
 */
export function deriveRemoteWorktreeLaunchProgress(
  replica: CmmdRuntimeThreadReplica | null,
): WorktreeLaunchProgress | null {
  const activities = (replica?.events ?? []).flatMap((event) => {
    if (event.kind !== "tool_call") return [];
    const kind = decodeCmmdRuntimeWorktreeLaunchActivityKind(event.payload);
    return kind === null ? [] : [{ kind, createdAt: event.occurredAt, payload: event.payload }];
  });
  const agentStartedAt = activities.findLast(
    (activity) => activity.kind === "launch.agent-starting",
  )?.createdAt;
  return deriveWorktreeLaunchProgress(activities, agentStartedAt ?? null, replicatedStop(replica));
}

/**
 * A provider start failure is replicated as an error_summary, never as a
 * launch row, and a cloud thread that failed at start has no turn to read a
 * state from. Without this the "Start agent" stage spun forever after the
 * failure had already been shown (#3310). Only a failure recorded after the
 * latest launch request counts; older failures belong to earlier attempts.
 */
function replicatedStop(replica: CmmdRuntimeThreadReplica | null): LaunchStop | undefined {
  const events = replica?.events ?? [];
  const requestedAt = events.findLast(
    (event) =>
      event.kind === "tool_call" &&
      decodeCmmdRuntimeWorktreeLaunchActivityKind(event.payload) === "worktree.requested",
  )?.occurredAt;
  if (!requestedAt) return undefined;
  const failure = events.findLast(
    (event) => event.kind === "error_summary" && event.occurredAt >= requestedAt,
  );
  return failure
    ? { state: "error", completedAt: failure.occurredAt, detail: failure.text ?? "" }
    : undefined;
}
